import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Mail, RefreshCw, LogOut, MessageSquare, Lock, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "bcw_admin_token";

const AdminPage = () => {
  const [token, setToken] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  useEffect(() => {
    const saved = sessionStorage.getItem(TOKEN_KEY);
    if (saved) setToken(saved);
  }, []);

  useEffect(() => {
    if (token) fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setSubmissions([]);
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubmissions(res.data || []);
      setLastFetch(new Date());
    } catch (err) {
      if (err.response?.status === 401) {
        // Token expired/invalid — force re-login
        logout();
        setError("Session expired. Please sign in again.");
      } else {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B2B40] flex items-center justify-center p-6">
        <form
          onSubmit={login}
          className="bg-white border-2 border-black hard-shadow-orange w-full max-w-md p-8"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#D9480F] flex items-center justify-center border-2 border-black">
              <Lock className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D9480F] font-bold">
                Blue Collar World · Admin
              </p>
              <h1 className="font-display uppercase text-2xl tracking-tight">
                Lead Inbox
              </h1>
            </div>
          </div>
          <label className="block font-mono text-[11px] uppercase tracking-[0.2em] font-bold mb-2">
            Password
          </label>
          <input
            type="password"
            data-testid="admin-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full border-2 border-black px-4 py-3 bg-[#F4F4F5] focus:bg-white outline-none font-medium mb-3"
            placeholder="Enter admin password"
            autoFocus
            required
          />
          {error && (
            <p className="font-mono text-xs text-[#D9480F] mb-3" data-testid="admin-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-submit"
            disabled={submitting}
            className="w-full bg-[#0B2B40] text-white px-6 py-3 font-display uppercase tracking-tight hover:bg-[#143b58] border-2 border-black disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Signing In...
              </>
            ) : (
              "Sign In"
            )}
          </button>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-center mt-4">
            Authorized personnel only
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <header className="bg-[#0B2B40] text-white border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F59E0B] font-bold">
              Blue Collar World · Admin Panel
            </p>
            <h1 className="font-display uppercase text-2xl md:text-3xl tracking-tight">
              Lead Inbox
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchSubmissions}
              disabled={loading}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 bg-[#D9480F] text-white px-4 py-2.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#E8590C] border-2 border-black disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-transparent text-white px-4 py-2.5 font-mono text-xs uppercase tracking-[0.2em] border-2 border-white hover:bg-white hover:text-black"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center justify-between mb-6">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600">
            <span data-testid="admin-count" className="text-[#D9480F] font-bold">
              {submissions.length}
            </span>{" "}
            submission{submissions.length === 1 ? "" : "s"} ·{" "}
            {lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : "Loading…"}
          </div>
        </div>

        {submissions.length === 0 && !loading ? (
          <div className="bg-white border-2 border-black p-16 text-center hard-shadow-sm">
            <MessageSquare className="w-12 h-12 text-zinc-300 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display uppercase text-2xl tracking-tight mb-2">
              No leads yet
            </h3>
            <p className="text-zinc-500 text-sm">
              When customers submit the contact form, they'll appear here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {submissions.map((s, i) => (
              <article
                key={s.id || i}
                data-testid={`admin-submission-${i}`}
                className="bg-white border-2 border-black p-6 hard-shadow-sm flex flex-col"
              >
                <div className="flex items-start justify-between mb-3 pb-3 border-b border-zinc-300">
                  <div>
                    <div className="font-display uppercase text-lg tracking-tight leading-tight">
                      {s.name}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#D9480F] mt-1 font-bold">
                      · {s.service_type}
                    </div>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 text-right">
                    {new Date(s.created_at).toLocaleDateString()}
                    <br />
                    {new Date(s.created_at).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <a
                    href={`tel:${s.phone.replace(/\D/g, "")}`}
                    className="flex items-center gap-2 text-sm hover:text-[#D9480F]"
                  >
                    <Phone className="w-3.5 h-3.5 text-[#D9480F]" strokeWidth={2.5} />
                    <span className="font-mono">{s.phone}</span>
                  </a>
                  <a
                    href={`mailto:${s.email}`}
                    className="flex items-center gap-2 text-sm hover:text-[#D9480F] break-all"
                  >
                    <Mail className="w-3.5 h-3.5 text-[#D9480F]" strokeWidth={2.5} />
                    <span className="font-mono">{s.email}</span>
                  </a>
                </div>

                <div className="bg-[#F4F4F5] border-l-2 border-[#D9480F] p-3 text-sm text-zinc-700 leading-relaxed flex-1">
                  {s.message}
                </div>

                <div className="mt-4 flex gap-2">
                  <a
                    href={`tel:${s.phone.replace(/\D/g, "")}`}
                    className="flex-1 bg-[#D9480F] text-white text-center px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#E8590C] border-2 border-black"
                  >
                    Call Back
                  </a>
                  <a
                    href={`mailto:${s.email}`}
                    className="flex-1 bg-white text-black text-center px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-zinc-100 border-2 border-black"
                  >
                    Reply
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
